import { useState } from "react";
import { useParams } from "react-router-dom";
import Room from "@/components/Room";
import NotFound from "@/pages/NotFound";
import { ROOMS } from "@/lib/site";

const API = process.env.REACT_APP_BACKEND_URL;
const WORK_ADDRESS_MESSAGE = "Please use your work address.";
const FREE_DOMAINS = ["gmail.com", "googlemail.com", "yahoo.", "ymail.com", "rocketmail.com", "hotmail.", "outlook.", "live.", "msn.com", "icloud.com", "me.com", "mac.com", "aol.com", "proton.me", "protonmail.com", "pm.me", "gmx.", "yandex.", "mail.com", "mail.ru", "qq.com", "163.com", "126.com", "sina.com", "zoho.com", "fastmail.com", "hey.com", "tutanota.com", "tuta.io", "inbox.com", "email.com", "usa.com", "hushmail.com"];

function isFreeDomain(email) {
  const domain = email.trim().toLowerCase().split("@").pop();
  return FREE_DOMAINS.some((d) => (d.endsWith(".") ? domain.startsWith(d) || domain.includes("." + d) : domain === d || domain.endsWith("." + d)));
}

async function post(path, body) {
  const res = await fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.detail || "Something went wrong."), { field: data.field });
  return data;
}

const FIELDS = [
  ["name", "Name", "text", "name"],
  ["organisation", "Organisation", "text", "organization"],
  ["role", "Role", "text", "organization-title"],
  ["work_email", "Work email", "email", "email"],
];

export default function Enter() {
  const { door } = useParams();
  const room = ROOMS.find((r) => r.slug === door);
  const [form, setForm] = useState({ name: "", organisation: "", role: "", work_email: "", why_it_matters: "", website: "" });
  const [stage, setStage] = useState("form");
  const [intakeId, setIntakeId] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!room) return <NotFound />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const send = async (e) => {
    e.preventDefault();
    setError(null);
    if (isFreeDomain(form.work_email)) return setError({ field: "work_email", message: WORK_ADDRESS_MESSAGE });
    setBusy(true);
    try {
      const data = await post("/api/intake/start", { door, ...form });
      setIntakeId(data.intake_id);
      setStage("code");
    } catch (err) {
      setError({ field: err.field, message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await post("/api/intake/confirm", { intake_id: intakeId, code: code.trim() });
      setStage("done");
    } catch (err) {
      setError({ field: "code", message: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Room title={room.label} description="">
      <div className="fade intake" data-testid={`enter-${door}`}>
        <div className="kicker" data-testid="enter-label">{room.label}</div>

        {stage === "form" && (
          <form onSubmit={send} data-testid="intake-form">
            {FIELDS.map(([key, label, type, auto]) => (
              <div className="field" key={key}>
                <label htmlFor={`f-${key}`}>{label}</label>
                <input id={`f-${key}`} type={type} autoComplete={auto} required value={form[key]} onChange={set(key)} data-testid={`intake-${key}`} />
                {error?.field === key && <div className="note" role="alert" data-testid={`intake-${key}-error`}>{error.message}</div>}
              </div>
            ))}
            <div className="honey" aria-hidden="true">
              <label htmlFor="f-website">Website</label>
              <input id="f-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="intake-honeypot" />
            </div>
            <div className="why">
              <h2 id="why-heading">Why does this matter?</h2>
              <textarea aria-labelledby="why-heading" required value={form.why_it_matters} onChange={set("why_it_matters")} data-testid="intake-why" />
              {error?.field === "why_it_matters" && <div className="note" role="alert">{error.message}</div>}
            </div>
            {error && !FIELDS.some(([k]) => k === error.field) && error.field !== "why_it_matters" && (
              <div className="note" role="alert" data-testid="intake-error">{error.message}</div>
            )}
            <button className="send" type="submit" disabled={busy} data-testid="intake-send">Send</button>
          </form>
        )}

        {stage === "code" && (
          <form onSubmit={confirm} data-testid="code-form">
            <p className="state" data-testid="code-sent">A code has been sent to your work address.</p>
            <div className="field">
              <label htmlFor="f-code">Code</label>
              <input id="f-code" className="code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} data-testid="intake-code" />
              {error && <div className="note" role="alert" data-testid="intake-code-error">{error.message}</div>}
            </div>
            <button className="send" type="submit" disabled={busy || code.trim().length !== 6} data-testid="intake-confirm">Confirm</button>
          </form>
        )}

        {stage === "done" && (
          <p className="state" data-testid="intake-received">Received. Every answer receives a reply.</p>
        )}
      </div>
    </Room>
  );
}
